import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X } from "lucide-react";

const EMPTY = { medication: "", center_id: "", notes: "" };

export default function AdminAutomation() {
  const [rules, setRules] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [vetMode, setVetMode] = useState("round_robin");

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, s] = await Promise.all([
        api.get("/admin/automation-rules"),
        api.get("/admin/fulfillment-centers"),
        api.get("/admin/settings"),
      ]);
      setRules(r.data || []);
      setCenters(c.data || []);
      setVetMode(s.data?.vet_assignment_mode || "round_robin");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post("/admin/automation-rules", form); toast.success("Rule added"); setCreating(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    try { await api.delete(`/admin/automation-rules/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const updateVetMode = async (mode) => {
    try { await api.put("/admin/settings", { vet_assignment_mode: mode }); setVetMode(mode); toast.success("Vet routing updated"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl" data-testid="admin-automation">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">Routing</p>
          <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Automation Rules</h1>
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-2">Decide how prescriptions and orders flow through PawLinx automatically.</p>
        </div>
        <button onClick={() => setCreating(true)} className="flex items-center gap-2 px-4 h-10 rounded-lg bg-paw-accent hover:bg-paw-accent-hover text-white font-manrope text-sm font-semibold transition-colors" data-testid="rule-add">
          <Plus className="w-4 h-4" /> New Rule
        </button>
      </div>

      <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-6 mb-6" data-testid="vet-routing-section">
        <p className="font-outfit font-semibold text-base mb-1">Vet assignment</p>
        <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mb-4">How prescriptions are distributed across active vets.</p>
        <div className="flex gap-2 flex-wrap">
          {[
            { v: "round_robin", l: "Round Robin" },
            { v: "least_loaded", l: "Least Loaded" },
            { v: "manual", l: "Manual" },
          ].map(({ v, l }) => (
            <button key={v} onClick={() => updateVetMode(v)} className={`px-4 h-9 rounded-lg text-sm font-manrope transition-colors ${vetMode === v ? "bg-paw-accent text-white" : "bg-black/[0.03] dark:bg-white/5 hover:bg-black/[0.06] dark:hover:bg-white/10 border border-paw-border dark:border-white/10 text-paw-text-secondary dark:text-white/70"}`} data-testid={`vet-mode-${v}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-6">
        <p className="font-outfit font-semibold text-base mb-1">Medication → Fulfillment Centre</p>
        <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mb-4">When an order matches the medication, route it to the chosen centre.</p>

        {loading ? (
          <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : rules.length === 0 ? (
          <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40">No routing rules configured. Orders will use the default centre.</p>
        ) : (
          <table className="w-full">
            <thead className="bg-black/[0.02] dark:bg-white/[0.02] border-b border-paw-border dark:border-white/5">
              <tr className="text-left">
                <th className="px-3 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Medication</th>
                <th className="px-3 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Centre</th>
                <th className="px-3 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium">Notes</th>
                <th className="px-3 py-3 font-manrope text-xs uppercase tracking-wider text-paw-text-muted dark:text-white/40 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.rule_id} className="border-b border-paw-border dark:border-white/5 last:border-0" data-testid={`rule-row-${r.rule_id}`}>
                  <td className="px-3 py-3 font-manrope text-sm font-medium">{r.medication}</td>
                  <td className="px-3 py-3 font-manrope text-sm">{centers.find((c) => c.center_id === r.center_id)?.name || r.center_id}</td>
                  <td className="px-3 py-3 font-manrope text-sm text-paw-text-secondary dark:text-white/60">{r.notes || "n/a"}</td>
                  <td className="px-3 py-3 text-right">
                    <button onClick={() => remove(r.rule_id)} className="p-2 hover:bg-red-500/10 rounded-md transition-colors" data-testid={`rule-delete-${r.rule_id}`}><Trash2 className="w-4 h-4 text-red-700 dark:text-red-400" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setCreating(false)}>
          <form onSubmit={create} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="rule-form">
            <div className="flex justify-between items-center mb-5">
              <h3 className="font-outfit font-bold text-xl">New Routing Rule</h3>
              <button type="button" onClick={() => setCreating(false)} className="p-1 hover:bg-black/[0.03] dark:hover:bg-white/5 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Medication</label>
                <input required value={form.medication} onChange={(e) => setForm({ ...form, medication: e.target.value })} placeholder="e.g., Apoquel" className="w-full h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="rule-input-medication" />
              </div>
              <div className="space-y-1">
                <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Fulfillment Centre</label>
                <select required value={form.center_id} onChange={(e) => setForm({ ...form, center_id: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="rule-input-center">
                  <option value="">Select…</option>
                  {centers.map((c) => <option key={c.center_id} value={c.center_id}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="font-manrope text-xs text-paw-text-secondary dark:text-white/60 uppercase tracking-wider">Notes (optional)</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full h-10 px-3 rounded-lg bg-black/[0.03] dark:bg-white/5 border border-paw-border dark:border-white/10 text-sm font-manrope focus:outline-none focus:border-paw-accent" data-testid="rule-input-notes" />
              </div>
            </div>
            <button type="submit" disabled={saving} className="w-full mt-5 h-11 bg-paw-accent hover:bg-paw-accent-hover text-white disabled:opacity-60 rounded-lg font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="rule-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Rule"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
